import React, { useState } from 'react';
import { useNotify } from 'react-admin';
import { clsx } from 'clsx';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { http } from '@network';
import { DownloadFileIcon } from '@pages/credit-risk/export/icons/DownloadFileIcon';
import { User } from '@types';
import { axiosErrorToString } from '@utils';
import cls from './RegistrationFiles.module.css';

interface Props {
  registrationId: User['registrationId'];
}

const Icon = ({ value }: { value: boolean }) => value
  ? <CheckCircleFilled className={clsx(cls.verificationIconValid, cls.icon)}/>
  : <CloseCircleFilled className={clsx(cls.verificationIconInvalid, cls.icon)}/>;

export const RegistrationFiles = ({ registrationId }: Props) => {
  const isValid = registrationId?.value || false;
  const [isDownloading, setIsDownloading] = useState(false);
  const notify = useNotify();

  const handleDownload = async () => {
    if (isDownloading) return;

    try {
      setIsDownloading(true);
      const files = (registrationId?.fileUrls || []);

      if (files.length === 0) {
        notify('No registration files found', { type: 'warning' });
        return;
      }

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.url) continue;

        const res = await http.get<Blob>(`${file.url}`, { responseType: 'blob' });
        const blob = res.data as Blob;
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        if (i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      notify('Files downloaded successfully');
    } catch (error) {
      notify(axiosErrorToString(error), { type: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={clsx(cls.wrap, isValid ? cls.wrapValid : cls.wrapInvalid)}>
      <div className={cls.title}>Registration ID</div>
      <div className={cls.row}>
        <div className={clsx(cls.textCol, cls.col)}><Icon value={isValid} /></div>
        <div className={clsx(cls.textCol, cls.col)}>{isValid ? 'Completed' : 'Incomplete'} ({registrationId?.count || 0})</div>

        <button
          className={cls.download}
          type="button"
          onClick={handleDownload}
          disabled={isDownloading || !isValid}
          title={isValid ? 'Download registration files' : 'No files available'}
        >
          <DownloadFileIcon />
        </button>
      </div>
    </div>
  );
};
